import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gift, Share2, Copy, Users, Sparkles, Trophy } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

function makeCode(seed) {
  // Simple deterministic code from user id/email
  const s = String(seed || 'guest');
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) % 100000; }
  return 'GSK' + String(h).padStart(5, '0');
}

const rewards = [
  { count: 1, title: '୧ Refer', reward: '୭ ଦିନ Premium ମାଗଣା', rewardEn: '7 days Premium free', icon: '🎁', color: 'from-emerald-500 to-teal-600' },
  { count: 3, title: '୩ Refer', reward: '୧ ମାସ Premium ମାଗଣା', rewardEn: '1 month Premium free', icon: '🏆', color: 'from-amber-500 to-orange-600' },
  { count: 10, title: '୧୦ Refer', reward: '୩ ମାସ Premium + Certificate', rewardEn: '3 months + Certificate', icon: '👑', color: 'from-fuchsia-500 to-purple-600' },
];

export default function ReferralPage() {
  const { user, login } = useAuth();
  const [count, setCount] = useState(0);
  const code = makeCode(user?.user_id || user?.email);
  const shareUrl = `${window.location.origin}/?ref=${code}`;
  const message = `Namaskar 🙏\n\n${user?.name || 'ମୁଁ'} ଆପଣଙ୍କୁ Gurusevakendra ରେ join କରିବାକୁ ଆମନ୍ତ୍ରଣ କରୁଛନ୍ତି — Odisha ର ସର୍ବଶ୍ରେଷ୍ଠ Navodaya + Board + Teacher study platform।\n\nମୋ referral code: *${code}*\nSign up: ${shareUrl}`;

  useEffect(() => {
    try {
      const c = parseInt(localStorage.getItem('gsk_ref_count') || '0', 10);
      setCount(c);
    } catch (e) { console.warn('referral load failed', e); }
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(code);
    toast({ title: '📋 Code କପି ହୋଇଛି', description: code });
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Gurusevakendra', text: message, url: shareUrl });
        // Increment local counter (simulated)
        const c = count + 1;
        localStorage.setItem('gsk_ref_count', String(c));
        setCount(c);
      } catch (e) { console.warn('referral action failed', e); }
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(wa, '_blank');
    }
  };

  const nextReward = rewards.find((r) => count < r.count) || rewards[rewards.length - 1];
  const percent = Math.min(100, Math.round((count / nextReward.count) * 100));

  return (
    <div className="px-4 pt-4 space-y-4">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #d946ef 45%, #7c3aed 100%)' }}>
        <div className="absolute -top-6 -right-6 opacity-20"><Gift size={110} /></div>
        <Gift size={26} />
        <h1 className="text-xl font-extrabold mt-2">ସାଙ୍ଗଙ୍କୁ Invite କରନ୍ତୁ</h1>
        <p className="text-xs text-white/90 mt-1">Refer & Earn Premium · ୟ୍ୟୁ ୱିନ, ସାଙ୍ଗ ୱିନ</p>
      </div>

      {!user ? (
        <Card className="rounded-2xl p-6 border-gray-200 text-center">
          <div className="font-bold text-gray-800">Login କରି Referral Code ପାଆନ୍ତୁ</div>
          <Button onClick={login} className="mt-3 bg-emerald-600 text-white">Google Login</Button>
        </Card>
      ) : (
        <>
          {/* Code Card */}
          <Card className="rounded-2xl p-5 border-emerald-200 bg-emerald-50 text-center">
            <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">ଆପଣଙ୍କ Referral Code</div>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="text-3xl font-black text-emerald-800 font-mono tracking-wider">{code}</span>
              <button onClick={copy} className="w-8 h-8 rounded-lg bg-white border border-emerald-300 text-emerald-700 flex items-center justify-center"><Copy size={14} /></button>
            </div>
            <Button onClick={share} className="mt-4 w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2">
              <Share2 size={16} /> WhatsApp ରେ Share କରନ୍ତୁ
            </Button>
          </Card>

          {/* Progress */}
          <Card className="rounded-2xl p-4 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <div className="font-bold text-gray-800 flex items-center gap-1"><Users size={14} /> Successful Referrals</div>
              <div className="text-2xl font-extrabold text-emerald-600">{count}</div>
            </div>
            <div className="mt-2 text-[11px] text-gray-500">ପରବର୍ତ୍ତୀ ପୁରସ୍କାର: <strong>{nextReward.title}</strong> — {nextReward.reward}</div>
            <div className="h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" style={{ width: `${percent}%` }} />
            </div>
          </Card>

          {/* Rewards ladder */}
          <div>
            <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-1.5">
              <Trophy size={16} className="text-amber-500" /> ପୁରସ୍କାର ତାଲିକା
            </h3>
            <div className="space-y-2 mt-3">
              {rewards.map((r) => {
                const unlocked = count >= r.count;
                return (
                  <Card key={r.count} className={`rounded-2xl p-4 border-2 ${unlocked ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${r.color} text-white flex items-center justify-center text-2xl shadow-md`}>
                        {r.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-sm text-gray-900">{r.title}</div>
                        <div className="text-[11px] text-gray-700">{r.reward}</div>
                        <div className="text-[10px] text-gray-500">{r.rewardEn}</div>
                      </div>
                      {unlocked ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white flex items-center gap-1">
                          <Sparkles size={10} /> Unlocked
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Locked</span>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          <Card className="rounded-2xl p-3 border-amber-200 bg-amber-50">
            <p className="text-[11px] text-amber-800 leading-relaxed">
              <strong>💡 କେମିତି କାମ କରେ:</strong> ଆପଣଙ୍କ code use କରି ଯିଏ signup କରନ୍ତି, ସେ ପ୍ରଥମ purchase କରିଲେ ଆପଣଙ୍କୁ ପୁରସ୍କାର ମିଳିବ। Manual verification ପାଇଁ WhatsApp କରନ୍ତୁ +91 73258 93950।
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
